import {
  notNullish,
  parseBoolean,
  parseDefaultString,
  parseString,
  parsingRecordAs,
  parsingStringUnion,
} from "@de-xima/xima-common-js-lang";
import type Framebus from "framebus";
import type { FramebusSubscriberArg } from "framebus/dist/lib/types.d.ts";
import jQuery from "jquery";

import { InsertHints, type IFramebusChildMaskPanelOptions, type IInsertData, type IMaskPanel } from "../../Header.ts";
import { AbstractMaskPanel } from "../AbstractMaskPanel.ts";

const parseInsertData = parsingRecordAs({
  content: parseDefaultString,
  insertHint: parsingStringUnion(InsertHints),
});

const parseOnInsertData = parsingRecordAs({
  target: parseString,
  opts: parsingRecordAs({
    close: parseBoolean,
    placeholder: parseInsertData,
  }),
});

/**
 * Mask panel that takes talks with the top mask panel via a framebus. For the
 * child window where the actual mask panel is not located.
 *
 * Communication is like this:
 *
 * ```text
 *                   TopMask
 *                           \
 *                            \
 *                             ---- TopPanel ---- DialogPanel
 *                            /
 *                           / via framebus
 *                          /
 * ChildMask ---- ChildPanel
 */
export class FramebusChildMaskPanel extends AbstractMaskPanel implements IMaskPanel {
  /**
   * Framebus for communicating with the top frame.
   */
  private _framebus: Framebus;

  private _target: string;

  /**
   * @param options Options to configure this instance.
   */
  constructor(options: IFramebusChildMaskPanelOptions) {
    super();
    this._framebus = options.framebus;
    this._target = window.location.href;
    this._registerListeners();
  }

  override close(): void {
    this._framebus.emit("placeholderDialog", { action: "close", target: this._target });
  }

  override focused(): void {
    this._framebus.emit("placeholderDialog", { action: "focused", target: this._target });
  }

  override async getSelectedValue(): Promise<IInsertData | undefined> {
    return new Promise((resolve) => {
      this._framebus.emit("placeholderDialog", { action: "getSelectedValue", target: this._target }, (reply) => {
        resolve(notNullish(reply) ? parseInsertData(reply) : undefined);
      });
    });
  }

  override async isOpen(): Promise<boolean> {
    return new Promise((resolve) => {
      this._framebus.emit("placeholderDialog", { action: "isOpen", target: this._target }, (reply) => {
        resolve(parseBoolean(reply));
      });
    });
  }

  override onExternalChange(): void {
    this._framebus.emit("placeholderDialog", { action: "externalChange", target: this._target });
  }

  override open(): void {
    this._framebus.emit("placeholderDialog", { action: "open", target: this._target });
  }

  override update(): void {
    this._framebus.emit("placeholderDialog", { action: "update", target: this._target });
  }

  private _onInsert(data: FramebusSubscriberArg): void {
    const { opts, target } = parseOnInsertData(data);

    // Placeholder should be inserted in some other child iframe
    if (this._target !== target) {
      return;
    }
    // Do not insert placeholder if our iframe is not even visible
    // (e.g. because another tab is selected).
    if (!jQuery(document.body).is(":visible")) {
      return;
    }
    this._insertionListener({
      close: typeof opts.close === "boolean" ? opts.close : false,
      placeholder: opts.placeholder,
    });
  }

  private _registerListeners(): void {
    this._framebus.on("placeholderDialogInsert", (data) => this._onInsert(data));
  }
}
